/**
 * File: delete_filter_article_list.tsx
 * Description: Admin component that renders a grouped list of FAQ articles by category,
 * with the ability to delete individual articles via a confirmation dialog.
 */
import { useState } from 'react';
import MDEditor from '@uiw/react-md-editor';
import {
  Typography,
  Box,
  useTheme
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import type { Category, Article } from '@/interfaces/category';
import ConfirmDialog from '@/components/admin/confirm_dialog';
import ErrorDialog from '@/components/admin/error_dialog';
import CategoryArticlesList from '@/components/global/category_articles_list';
import CategoryArticlesListTextItem from '@/components/global/category_articles_list_text_item';
import { useThemeModeContext } from '@/context/theme/theme_mode_context';
import { ColorsPalletTheme } from "@/constants/colors_pallet_theme";

/**
 * Props for the FilterArticleList component.
 *
 * @property {Category[]} categories - List of categories, each containing their nested articles to display.
 * @property {(categoryId: number, articleId: number) => Promise<void>} onDeleteArticle - Deletes the given article. Owned by the page; must reject on failure so this component can surface the error dialog.
 * @property {() => void} onSuccess - Callback invoked after an article is successfully deleted.
 */
interface FilterArticleListProps {
  categories: Category[];
  onDeleteArticle: (categoryId: number, articleId: number) => Promise<void>;
  onSuccess: () => void;
}

/**
 * Identifies a specific article together with its parent category context,
 * used to target an article for deletion.
 *
 * @property {Article} article - The article selected for deletion.
 * @property {number} categoryId - Numeric identifier of the parent category.
 * @property {string} categoryTitle - Display title of the parent category.
 */
interface ArticleTarget {
  article: Article;
  categoryId: number;
  categoryTitle: string;
}

/**
 * Renders a grouped list of FAQ articles organised by category.
 * Each article row exposes a delete button that opens a confirmation dialog
 * before delegating the delete to onDeleteArticle. Errors are surfaced via an
 * error dialog.
 *
 * @param {Category[]} props.categories - Categories with their nested articles to display.
 * @param {(categoryId: number, articleId: number) => Promise<void>} props.onDeleteArticle - Deletes the targeted article; must reject on failure.
 * @param {() => void} props.onSuccess - Called after an article is successfully deleted so the parent can refresh its data.
 * @returns {JSX.Element} The rendered article list, or a fallback message when no articles are present.
 */
function DeleteFilterArticleList({ categories, onDeleteArticle, onSuccess }: FilterArticleListProps) {

  const [articleToDelete, setArticleToDelete] = useState<ArticleTarget | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Main App Theme
  const theme = useTheme();
  const colors = ColorsPalletTheme(theme.palette.mode);
  const { mode } = useThemeModeContext();

  /**
   * Delegates deletion of the currently targeted article to onDeleteArticle.
   * Closes the confirmation dialog on both success and failure; on failure it
   * opens the error dialog with the caught error message.
   *
   * @throws {Error} Caught internally — surfaces via the error dialog rather than propagating.
   */
  async function handleDelete() {
    if (!articleToDelete) return;
    setIsDeleting(true);
    try {
      await onDeleteArticle(articleToDelete.categoryId, articleToDelete.article.id);
      setArticleToDelete(null);
      setDeleteDialogOpen(false)
      onSuccess();
    } catch (error) {
      setArticleToDelete(null);
      setDeleteDialogOpen(false)
      setErrorMessage(error instanceof Error ? error.message : 'Error desconocido');
      setErrorDialogOpen(true);
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <>
      <CategoryArticlesList
        categories={categories}
        itemContent={(_category, article) => (
          <CategoryArticlesListTextItem
            title={article.title}
            creationDate={article.creation_date}
          />
        )}
        secondaryAction={(category, article) => (
          <IconButton
            edge="end"
            onClick={() => {
              setDeleteDialogOpen(true);
              setArticleToDelete({
                article,
                categoryId: category.id,
                categoryTitle: category.title,
              });
            }}
          >
            <DeleteIcon />
          </IconButton>
        )}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Confirmar eliminación"
        onClose={() => { setDeleteDialogOpen(false) }}
        onConfirm={() => { void handleDelete(); }}
        isSubmitting={isDeleting}
        confirmLabel="Eliminar"
        confirmColor="error"
        confirmSx={{
          backgroundColor: colors.redAccent[500],
          color: 'white',
          "&:hover": {
            background: colors.redAccent[400],
          }
        }}
      >
        <Typography><strong>Categoría:</strong> {articleToDelete?.categoryTitle}</Typography>
        <Typography sx={{ mt: 1 }}><strong>Artículo:</strong> {articleToDelete?.article.title}</Typography>
        <Typography sx={{ mt: 1 }}><strong>Contenido:</strong></Typography>
        <Box sx={{ mt: 1, p: 1, border: 2, borderColor: 'primary.main' }} data-color-mode={mode}>
          <MDEditor.Markdown source={String(articleToDelete?.article.content)} />
        </Box>
      </ConfirmDialog>

      <ErrorDialog
        open={errorDialogOpen}
        title="Error al eliminar el artículo"
        message={errorMessage}
        onClose={() => { setErrorDialogOpen(false) }}
      />
    </>
  );
}

export default DeleteFilterArticleList;
